import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Title, Text, Stack, Group, SimpleGrid, Paper, ThemeIcon, TextInput, Button } from '@mantine/core';
import { IconCalculator, IconGift, IconTicket, IconPackage, IconCrown, IconBuildingStore, IconReceipt2, IconUserSearch, IconSearch, IconArrowRight } from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthContext';
import './Home.css';

const groups = [
  { title: 'Consulta rápida', modules: [
    { title: 'Calculadora', path: '/calculadora', icon: IconCalculator, color: 'indigo', description: 'Calcular comisiones con adelantos y retiros de productos manuales.', keywords: 'comision porcentaje servicios boleta descuento saldo' },
  ] },
  { title: 'Giftcards y códigos', modules: [
    { title: 'Giftcards', path: '/gift-cards', icon: IconGift, color: 'yellow', description: 'Buscar, emitir y canjear giftcards. Revisar saldos e historial.', keywords: 'regalo prepago prepagado beneficiario correo venta' },
    { title: 'Solicitudes giftcards', path: '/giftcard-requests', icon: IconGift, color: 'orange', description: 'Revisar las solicitudes y compras de giftcards de la página.', keywords: 'web pago pendiente comprador' },
    { title: 'Cupones giftcards', path: '/giftcard-coupons', icon: IconReceipt2, color: 'yellow', description: 'Administrar los cupones de descuento para giftcards.', keywords: 'promocion descuento codigo' },
    { title: 'Códigos de convenio', path: '/codigos-convenio', icon: IconTicket, color: 'teal', description: 'Consultar y administrar códigos de convenio.', keywords: 'beneficio descuento empresa' },
  ] },
  { title: 'Tienda y suscripciones', modules: [
    { title: 'Pedidos tienda', path: '/product-orders', icon: IconPackage, color: 'blue', description: 'Revisar ventas de productos y gestionar sus retiros.', keywords: 'pagina compra web pedido entrega pago' },
    { title: 'Premium Club', path: '/premium-club', icon: IconCrown, color: 'red', description: 'Consultar suscripciones, pagos y beneficios Premium.', keywords: 'membresia socio caja box recompensa' },
    { title: 'Catálogo tienda', path: '/store-products', icon: IconBuildingStore, color: 'teal', description: 'Administrar productos, precios e imágenes de la tienda.', keywords: 'pagina venta catalogo stock', admin: true },
  ] },
  { title: 'Administración', modules: [
    { title: 'Deudas Club', path: '/deudas-club', icon: IconReceipt2, color: 'red', description: 'Consultar deudas y registrar sus movimientos.', keywords: 'abono pago saldo deuda', admin: true },
    { title: 'Postulaciones de barberos', path: '/barber-applications', icon: IconUserSearch, color: 'violet', description: 'Revisar postulaciones recibidas desde la página.', keywords: 'equipo trabajo postulacion barbero candidato', admin: true },
  ] },
];
const normalize = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

export default function Home() {
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const terms = normalize(search).split(/\s+/).filter(Boolean);
  const visibleGroups = groups.map((group) => ({ ...group, modules: group.modules.filter((module) =>
    (!module.admin || isAdmin) && terms.every((term) => normalize(`${module.title} ${module.description} ${module.keywords}`).includes(term))
  ) })).filter((group) => group.modules.length);
  const count = visibleGroups.reduce((total, group) => total + group.modules.length, 0);

  return (
    <Container size="xl" px={0}>
      <Stack gap="xl">
        <Stack gap="xs">
          <Text size="sm" c="dimmed" fw={600}>Inicio</Text>
          <Title order={1} fz={{ base: 26, sm: 34 }}>¿Qué necesitas hacer?</Title>
          <Text c="dimmed">Encuentra el módulo para gestionar giftcards, códigos y ventas de la página.</Text>
        </Stack>
        <Paper p={{ base: 'md', sm: 'lg' }} radius="lg" withBorder>
          <TextInput
            label="Buscar un módulo o una acción"
            placeholder="Ej.: canjear, pedido, descuento…"
            leftSection={<IconSearch size={20} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            size="md"
          />
          <Group justify="space-between" mt="sm" gap="xs">
            <Text size="sm" c="dimmed" role="status">{count} {count === 1 ? 'módulo disponible' : 'módulos disponibles'}</Text>
            {search && <Button variant="subtle" size="compact-sm" onClick={() => setSearch('')}>Limpiar búsqueda</Button>}
          </Group>
        </Paper>
        {visibleGroups.map((group) => (
          <Stack gap="md" key={group.title}>
            <Title order={2} fz="lg">{group.title}</Title>
            <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }} spacing="md">
              {group.modules.map(({ title, path, icon: Icon, color, description }) => (
                <Paper component={Link} to={path} key={path} p="lg" radius="lg" withBorder className="home-module-link">
                  <Group justify="space-between" mb="md">
                    <ThemeIcon color={color} variant="light" size={44} radius="md"><Icon size={24} /></ThemeIcon>
                    <IconArrowRight size={18} aria-hidden="true" />
                  </Group>
                  <Text fw={700} c="dark.8" mb={6}>{title}</Text>
                  <Text size="sm" c="dimmed" lh={1.5}>{description}</Text>
                </Paper>
              ))}
            </SimpleGrid>
          </Stack>
        ))}
        {!count && (
          <Paper p="xl" withBorder ta="center">
            <Text fw={700}>No encontramos un módulo con esa búsqueda</Text>
            <Text c="dimmed" mt="xs">Prueba con “giftcard”, “pedido” o “código”.</Text>
            <Button mt="md" variant="light" onClick={() => setSearch('')}>Ver todos los módulos</Button>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}
